// Playback hook for Starter Tape — wraps the shared Tone.js engine so any
// page (a tape, the sandbox, the shelf) can play a song, preview notes and
// drive playheads. Audio starts lazily on the first click that needs it.

import { useCallback, useEffect, useRef, useState } from "react";
import { getEngine } from "../../lib/starterTapeAudio";
import { totalSteps } from "../../lib/starterTape";

export default function useTapePlayer(song) {
  const [playing, setPlaying] = useState(false);
  const [waking, setWaking] = useState(false);
  const [error, setError] = useState(null);
  const engineRef = useRef(null);
  // Elements whose `left` follows the playhead; registered by components.
  const playheads = useRef(new Set());

  // Keep the engine's schedule in step with every edit.
  useEffect(() => {
    if (engineRef.current && song) engineRef.current.setSong(song);
  }, [song]);

  // Stop the sound when leaving the page.
  useEffect(
    () => () => {
      if (engineRef.current) engineRef.current.stop();
    },
    []
  );

  const steps = song ? totalSteps(song) : 1;

  // Move the playheads every frame while playing, without re-rendering.
  useEffect(() => {
    const els = () =>
      [...playheads.current].filter((el) => {
        if (el.isConnected) return true;
        playheads.current.delete(el); // unmounted
        return false;
      });
    if (!playing) {
      els().forEach((el) => (el.style.display = "none"));
      return undefined;
    }
    let frame;
    const tick = () => {
      const pos = engineRef.current && engineRef.current.position();
      els().forEach((el) => {
        if (pos === null) {
          el.style.display = "none";
        } else {
          el.style.display = "block";
          el.style.left = `${(pos / steps) * 100}%`;
        }
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing, steps]);

  const ensureEngine = useCallback(async () => {
    if (engineRef.current) return engineRef.current;
    setWaking(true);
    setError(null);
    try {
      engineRef.current = await getEngine();
      return engineRef.current;
    } catch (e) {
      setError("Couldn't start audio in this browser.");
      return null;
    } finally {
      setWaking(false);
    }
  }, []);

  const play = useCallback(
    async (songToPlay = song) => {
      const engine = await ensureEngine();
      if (!engine || !songToPlay) return;
      engine.setSong(songToPlay);
      engine.play();
      setPlaying(true);
    },
    [ensureEngine, song]
  );

  const stop = useCallback(() => {
    if (engineRef.current) engineRef.current.stop();
    setPlaying(false);
  }, []);

  const togglePlay = useCallback(
    () => (playing ? stop() : play()),
    [playing, play, stop]
  );

  const preview = useCallback(
    async (instrument, midi) => {
      const engine = await ensureEngine();
      if (!engine) return;
      if (song) engine.setSong(song); // so the preview uses the song's sound
      engine.preview(instrument, midi);
    },
    [ensureEngine, song]
  );

  // Ref callback factory: <div ref={registerPlayhead} /> follows playback.
  const registerPlayhead = useCallback((el) => {
    if (el) playheads.current.add(el);
  }, []);

  return {
    playing,
    waking,
    error,
    play,
    stop,
    togglePlay,
    preview,
    registerPlayhead,
  };
}
